/** Shared live Kitchen food-ticket queue (Phase 5E: reused by Kitchen + Coordinator). */
import { useEffect, useState, useCallback, useMemo } from "react";
import api, { formatErr } from "../lib/api";
import { useEventSocket } from "../lib/ws";
import { useMotif } from "../contexts/MotifContext";
import { toast } from "sonner";
import { Utensils, Check, Clock, ArrowRight } from "lucide-react";
import { formatTimestamp } from "../lib/time";
import { VipBadge } from "./VipBadge";
import { T, useI18n } from "../lib/i18n";

const STATUSES = ["new", "accepted", "in_progress", "delivered"];

export default function KitchenQueue({ embedded = false }) {
  useI18n();
  const { eventId } = useMotif();
  const [reqs, setReqs] = useState([]);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/requests/food-drink", { params: { category: "food" } });
      setReqs(data);
    } catch (e) { toast.error(formatErr(e)); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  useEventSocket(eventId, (msg) => {
    if (msg.type === "fd_request_new" && msg.payload.category === "food") {
      setReqs((r) => [msg.payload, ...r]);
      toast(`${T("New")}: ${msg.payload.item} · ${T("Table")} ${msg.payload.table_name}`, { icon: "🍽️" });
    }
    if (msg.type === "fd_request_update" && msg.payload.category === "food") {
      setReqs((r) => r.map((x) => (x.id === msg.payload.id ? msg.payload : x)));
    }
    if (msg.type === "ws_reconnected") refresh();
  });

  const update = async (id, status) => {
    try { await api.put(`/requests/food-drink/${id}/status`, { status }); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const counts = useMemo(
    () => STATUSES.reduce((a, s) => ({ ...a, [s]: reqs.filter((r) => r.status === s).length }), {}),
    [reqs]
  );

  // VIP first, then oldest first; delivered/cancelled hidden
  const activeReqs = useMemo(
    () => reqs
      .filter((r) => r.status !== "delivered" && r.status !== "cancelled")
      .sort((a, b) => {
        const av = a.vip ? 0 : 1, bv = b.vip ? 0 : 1;
        if (av !== bv) return av - bv;
        return (a.created_at || "").localeCompare(b.created_at || "");
      }),
    [reqs]
  );

  return (
    <div data-testid={embedded ? "coordinator-kitchen-queue" : "kitchen-queue"}>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {STATUSES.map((s) => (
          <div key={s} className="card-elev p-4">
            <div className="text-[10px] tracking-luxe text-motif-muted">{T(s.replace("_", " "))}</div>
            <div className="font-display text-3xl text-primary-motif">{counts[s] || 0}</div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {activeReqs.map((r) => (
          <TicketCard key={r.id} r={r} update={update} />
        ))}
        {reqs.length === 0 && <div className="text-motif-muted text-sm">{T("No active tickets.")}</div>}
      </div>
    </div>
  );
}

function TicketCard({ r, update }) {
  const next = { new: "accepted", accepted: "in_progress", in_progress: "delivered" };
  return (
    <div className="card-elev p-5 fadein" data-testid={`ticket-${r.id}`}>
      <div className="flex items-start justify-between mb-2">
        <div>
          <div className="font-display text-2xl text-motif leading-tight">{r.item}</div>
          <div className="text-xs text-motif-muted mt-1">
            <span className="text-primary-motif font-medium">{r.guest_name}</span> · {T("Table")} {r.table_name}
            {r.seat ? ` · ${T("Seat")} ${r.seat}` : ""}
          </div>
          {r.notes && <div className="text-xs text-motif mt-1 italic">{r.notes}</div>}
        </div>
        <Pill s={r.status} />
      </div>
      <div className="text-[10px] tracking-luxe text-motif-muted mb-3">
        <Clock className="w-3 h-3 inline mr-1" />
        {formatTimestamp(r.created_at)}
      </div>
      {next[r.status] && (
        <button
          onClick={() => update(r.id, next[r.status])}
          className="btn-primary w-full flex items-center justify-center gap-2"
          data-testid={`advance-${r.id}`}
        >
          {next[r.status] === "delivered" ? <Check className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />}
          {T("Mark")} {T(next[r.status].replace("_", " "))}
        </button>
      )}
    </div>
  );
}

function Pill({ s }) {
  const colors = {
    new: "var(--motif-text-muted)",
    accepted: "#3b82f6",
    in_progress: "#f59e0b",
    delivered: "#10b981",
    cancelled: "#ef4444",
  };
  return (
    <span className="text-[10px] tracking-luxe px-2 py-1 rounded-full" style={{ background: "var(--motif-accent)", color: colors[s] || "var(--motif-text)" }}>
      {T(s.replace("_", " "))}
    </span>
  );
}
